<template>
    <div class="accordion-container" :id="uniqueId">
        <div class="header" ref="header" @click="headerClick" :class="[ isOpen?'open':'', active?'active':'']">
            <label>{{ title }}</label>

            <slot name="header"></slot>

            <div class="icon">
                <slot name="icon">
                    <label v-if="active">{{ isOpen ? 'close' : 'open' }}</label>
                </slot>
            </div>
        </div>

        <div class="panel" :class="[{ 'open' : (isOpen && active) }, applyOverflow?'overflow':'']" :ref="uniqueId" :style="{ 'max-height' : maxHeight + 'px' }">
            <slot></slot>
        </div>
    </div>
</template>

<script>
export default {
    name: 'accordion-display',
    props: {
        title: {
            type: String,
            default: 'Title'
        },
        active: {
            type: Boolean,
            default: true
        },
        toggleable: {
            type: Boolean,
            default: true
        },
        initiallyOpen: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isOpen: false,
            uniqueId: Math.random().toString(36).substring(2, 9),
            maxHeight: 0,
            applyOverflow: false
        }
    },
    mounted() {
        if (this.initiallyOpen) {
            this.toggle()
        }
    },
    watch: {
        isOpen(newvalue) {
            this.$emit('update:open', newvalue)
        }
    },
    methods: {
        headerClick() {
            if (this.active && this.toggleable) {
                this.toggle()
                this.$emit('click')
            }
        },
        toggle() {
            let timeout = 0
            const element = this.$refs[this.uniqueId]

            this.isOpen = !this.isOpen

            if (this.isOpen) {
                // +16px padding bottom that is added when the panel is open
                this.maxHeight = element.scrollHeight + 16
                timeout = this.getTransitionDuration(element) + 1
            } else {
                this.maxHeight = 0
            }

            // if we open the panel, we wait for the transition (200ms) to end,
            // so we dont get a weird overflow, we need an extra property for this
            // because the panel must be open and the maxHeight already set for this to work
            setTimeout(() => {
                this.applyOverflow = this.isOpen
                const element = document.getElementById(this.uniqueId)
                if (this.isOpen && element) element.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'nearest'})
            }, timeout)
        },
        getTransitionDuration(element) {
            // unset duration === 0s, so we dont need any null/unset checks
            let transitionduration = window.getComputedStyle(element).transitionDuration

            if (transitionduration.indexOf('s')) {
                transitionduration = transitionduration.substring(0, transitionduration.length -1)
                transitionduration = transitionduration * 1000
            } else if (transitionduration.indexOf('ms')) {
                transitionduration = transitionduration.substring(0, transitionduration.length -1)
            }

            return transitionduration
        }
    }
}
</script>

<style lang="scss" scoped>
.accordion-container {
    box-shadow: 0px 2px 4px #00000022;
    background-color: #fff;
    border-radius: 8px;
    overflow: hidden;

    > .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        width: 100%;
        min-height: 60px;
        transition: all .2s ease;
        font-size: 1.5em;
        font-weight: bold;
        user-select: none;

        &:not(.active) {
            background-color: #ccc;
            color: #999;
            cursor: not-allowed;
        }

        &.open {
            border-bottom-left-radius: 0px;
            border-bottom-right-radius: 0px;
            
            > .icon {
                > :deep(img) {
                    transform: rotate(-180deg);
                }
            }
        }

        &.active {
            &:hover {
                &,
                :deep(*) {
                    cursor: pointer;
                }
            }
        }

        > .icon {
            fill: #064A6C;
            height: 28px;
            display: flex;
            align-items: center;

            > :deep(img) {
                transition: all .3s ease;
                height: 80%;
            }
        }
    }

    > .panel {
        padding: 0 16px 40px 16px;
        background-color: #fff;
        max-height: 0;
        overflow: hidden;
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
        /*
            no shorthand for the transition,
            so we can easily obtain the duration via js
        */
        transition-property: max-height;
        transition-duration: 200ms;
        transition-timing-function: ease-out;

        &.open.overflow {
            padding-bottom: 16px;
            overflow: visible;
        }
    }
}
</style>